import { CustomLineData } from './data';
import { CustomLineSeriesOptions } from './options';
import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas';
import { ICustomSeriesPaneRenderer, PaneRendererCustomData, PriceToCoordinateConverter, Time } from 'lightweight-charts';

interface CustomLineBarItem {
  x: number;
  y: number;
}

export class CustomLineSeriesRenderer<TData extends CustomLineData> implements ICustomSeriesPaneRenderer {
  _data: PaneRendererCustomData<Time, TData> | null = null;
  _options: CustomLineSeriesOptions | null = null;

  draw(target: CanvasRenderingTarget2D, priceConverter: PriceToCoordinateConverter): void {
    target.useBitmapCoordinateSpace(scope => this._drawImpl(scope, priceConverter));
  }

  update(data: PaneRendererCustomData<Time, TData>, options: CustomLineSeriesOptions): void {
    this._data = data;
    this._options = options;
  }

  _drawImpl(renderingScope: BitmapCoordinatesRenderingScope, priceToCoordinate: PriceToCoordinateConverter): void {
    if (this._data === null || this._data.bars.length === 0 || this._data.visibleRange === null || this._options === null) {
      return;
    }
    const ctx = renderingScope.context;
    const options = this._options;
    const bars: CustomLineBarItem[] = this._data.bars.map(bar => {
      return {
        x: Math.round(bar.x * renderingScope.horizontalPixelRatio),
        y: priceToCoordinate(bar.originalData.value)! * renderingScope.verticalPixelRatio,
      };
    });
    const firstBar = bars[this._data.visibleRange.from];
    const getRangeColor = (index: number): string => {
      if (typeof options.customRange === 'string') return options.lineColor;
      const foundRange = index > options.customRange.from && index <= options.customRange.to;
      if (foundRange) {
        return options.customColor;
      }
      return options.lineColor;
    };
    const rangeColors: string[] = new Array(this._data.visibleRange.to);
    let minY = firstBar.y;
    for (let i = this._data.visibleRange.from + 1; i < this._data.visibleRange.to; i++) {
      rangeColors[i] = getRangeColor(i);
      const bar = bars[i];
      if (bar.y < minY) minY = bar.y;
    }
    // Draw line
    let previousPosition = [firstBar.x, firstBar.y];
    for (let i = this._data.visibleRange.from + 1; i < this._data.visibleRange.to; i++) {
      const bar = bars[i];
      if (bar.y < minY) minY = bar.y;
      const rangeColor = getRangeColor(i);
      const rangeColorChanged = i > 0 ? rangeColors[i - 1] !== rangeColor : false;
      const rangeColorWillChange = i === this._data.visibleRange.to - 1 ? true : rangeColors[i + 1] !== rangeColor;
      if (rangeColorChanged) {
        ctx.beginPath();
        ctx.moveTo(previousPosition[0], previousPosition[1]);
      }
      ctx.lineTo(bar.x, bar.y);
      if (rangeColorWillChange) {
        ctx.strokeStyle = rangeColor;
        ctx.lineWidth = options.lineWidth * renderingScope.verticalPixelRatio;
        ctx.stroke();
      }
      previousPosition = [bar.x, bar.y];
    }
  }
}
